/** @forge-header
 * @id          ncaaParsers
 * @role        NCAA ticker parsers | men's/women's basketball game moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:parseFallback,splitOnNamedTeam
 * @downstream  kalshiTicker/parse
 * @exports     parseNcaaMbGame:fn | parseNcaaWbGame:fn | parseNcaaGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitOnNamedTeam } from '../helpers';

/** NCAA Men's Basketball: KXNCAAMBGAME-26NOV02CCSUTEM-TEM */
export function parseNcaaMbGame(raw: string): ParsedTicker {
  return parseNcaaGameGeneric(raw, 'KXNCAAMBGAME', 'NCAAMB');
}

/** NCAA Women's Basketball: KXNCAAWBGAME-... */
export function parseNcaaWbGame(raw: string): ParsedTicker {
  return parseNcaaGameGeneric(raw, 'KXNCAAWBGAME', 'NCAAWB');
}

/** NCAA generic game: KXNCAAGAME-... */
export function parseNcaaGame(raw: string): ParsedTicker {
  return parseNcaaGameGeneric(raw, 'KXNCAAGAME', 'NCAA');
}

/** Generic NCAA game parser */
function parseNcaaGameGeneric(raw: string, series: string, label: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  // 7 date + a pack of at least 4 letters (NEBU is two 2-letter codes).
  if (mid.length < 11) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCode = parts[2] ?? null;

  // College codes run 2 to 5 letters, so only the named team proves the cut.
  const split = splitOnNamedTeam(mid.slice(7), teamCode);
  if (!split) {
    return parseFallback(raw);
  }

  const [awayCode, homeCode] = split;

  const game: GameInfo = { awayCode, homeCode };

  const shortLabel = `${label}: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `${label}: ${awayCode} @ ${homeCode} · ${teamCode} Win`
    : `${label}: ${awayCode} @ ${homeCode}`;

  return {
    raw,
    category: 'ncaa-game',
    series,
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}
